import {
  enableProdMode,
  ErrorHandler,
  APP_INITIALIZER,
  Injector,
  importProvidersFrom,
  provideZoneChangeDetection,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { QRCodeModule } from 'angularx-qrcode';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { AccreditationResolver } from './app/service/accreditation.resolver';
import { OriginatorBusinessResolver } from './app/service/originator-business.resolver';
import { AggregatorResolver } from './app/service/aggregator.resolver';
import { RatecardsResolver } from './app/service/ratecards-resolver.service';
import { CustomerUserResolver } from './app/service/customer-user.resolver';
import { UserResolver } from './app/service/user.resolver';
import { InsurancePremiumRatecardResolver } from './app/service/insurance-premium-ratecard.resolver';
import { BusinessOverdraftRatecardResolver } from './app/service/business-overdraft-ratecard.resolver';
import { BusinessLoansRatecardResolver } from './app/service/business-loans-ratecard.resolver';
import { AssetFinanceRatecardResolver } from './app/service/asset-finance-ratecard.resolver';
import { ApplicationResolver } from './app/service/application.resolver';
import { OfflineDetectionService } from './app/service/offline-detection.service';
import { DocusignService } from './app/service/docusign.service';
import { AppInsightService } from './app/service/app-insight.service';
import { LocalApplicationsDbService } from './app/service/local-applications-db.service';
import { ApplicationAuditService } from './app/service/applicationAudit.service';
import { AuditService } from './app/service/audit.service';
import { RegistrationService } from './app/service/registration.service';
import { AccreditationService } from './app/service/accreditation.service';
import { PpsrService } from './app/service/ppsr.service';
import { ConfigurationService } from './app/service/configuration.service';
import { FeatureFlagService } from './app/service/feature-flag.service';
import { NotificationService } from './app/service/notification.service';
import { AdminService } from './app/service/admin.service';
import { BankingService } from './app/service/banking.service';
import { BasiqService } from './app/service/basiq.service';
import { TradeAdminService } from './app/service/trade-admin.service';
import { DigitalIdService } from './app/service/digital-id.service';
import { ApplicationService } from './app/service/application.service';
import { RedbookService } from './app/service/redbook.service';
import { AutoRefreshTokenInterceptorFn } from './app/service/auto-refresh-token.interceptor';
import { HttpErrorInterceptorFn } from './app/service/error-handler.interceptor';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { GlobalErrorHandler } from './app/service/global-error-handler';
import { PortalHotToastService, GrowUiLibraryModule } from '@portal-workspace/grow-ui-library';
import { TwoFactorAuthService } from './app/service/2fa.service';
import { AuthService } from './app/service/auth.service';
import { StorageService } from './app/service/storage.service';
import { OverdraftCustomerService } from './app/service/overdraft-customer.service';
import { HeartbeatService } from './app/service/heartbeat.service';

import { provideServiceWorker } from '@angular/service-worker';
import { UpdateService, UpdateInterceptorFn } from './app/service/update.service';
import { BankfeedsService } from "./app/service/bankfeeds.service";
import { setupComponentUtils } from '@portal-workspace/grow-ui-library';
import { BankStatementsUploadResolver } from './app/service/bank-statements-upload.resolver';
import { provideHotToastConfig } from "@ngneat/hot-toast";
import { AppRoutingModule } from './app/app-routes';
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { CreditFlowResolver } from './app/service/credit-flow.resolver';
import { BrokerflowService } from "./app/service/brokerflow.service";
import { WhitelabelService } from "./app/service/whitelabel.service";
import { PortalThemeService } from "./app/service/portal-theme.service";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IndustryService } from './app/service/industry.service';
import { CronJobService } from './app/service/cron-job.service';

const appInitializerFn = (injector: Injector, updateService: UpdateService, whitelabelService: WhitelabelService, themeService: PortalThemeService, heatbeatService: HeartbeatService, authService: AuthService, industryService: IndustryService) => {
  return () => new Promise((res, rej) => {
    setupComponentUtils({
      encryptionKey: environment.localStorage.encryptionKey,
    });
    whitelabelService.loadFromLocalStorageAndTriggerWhitelabelEvent();
    if (themeService) {
      themeService.loadFromLocalStorageAndTriggerThemeEvent();
    }
    try {
      heatbeatService.init();
      heatbeatService.subject.asObservable().pipe(
        takeUntilDestroyed(injector.get<DestroyRef>(DestroyRef))
      ).subscribe(r => {
        if (r.type === 'HeartbeatUserDisabledEvent') {
          authService.logout().subscribe(r => {
            injector.get<ChangeDetectorRef>(ChangeDetectorRef).detectChanges();
          });
        }
      });
    } catch (e) {
      console.error(`Error initializing heatbeat service`, e);
    }
    try {
      if (environment.serviceWorker.enable) {
        updateService.init();
      }
    } catch (e) {
      console.error(`Error initializing sw update service`, e);
    }
    res(true);
  });
}



if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(BrowserModule, GrowUiLibraryModule, QRCodeModule, AppRoutingModule),
    provideAnimations(),
    provideAnimationsAsync('animations'),
    provideZoneChangeDetection(),
    provideHttpClient(withInterceptors([
      HttpErrorInterceptorFn(),
      AutoRefreshTokenInterceptorFn(),
      UpdateInterceptorFn(),
    ])),
    { provide: StorageService, useClass: StorageService },
    { provide: AuthService, useClass: AuthService },
    { provide: TwoFactorAuthService, useClass: TwoFactorAuthService },
    { provide: PortalHotToastService, useClass: PortalHotToastService },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: RedbookService, useClass: RedbookService },
    { provide: ApplicationService, useClass: ApplicationService },
    { provide: DigitalIdService, useClass: DigitalIdService },
    { provide: TradeAdminService, useClass: TradeAdminService },
    { provide: AdminService, useClass: AdminService },
    { provide: BankingService, useClass: BankingService },
    { provide: NotificationService, useClass: NotificationService },
    { provide: ConfigurationService, useClass: ConfigurationService },
    { provide: FeatureFlagService, useClass: FeatureFlagService },
    { provide: PpsrService, useClass: PpsrService },
    { provide: AccreditationService, useClass: AccreditationService },
    { provide: RegistrationService, useClass: RegistrationService },
    { provide: AuditService, useClass: AuditService },
    { provide: ApplicationAuditService, useClass: ApplicationAuditService },
    { provide: LocalApplicationsDbService, useClass: LocalApplicationsDbService },
    { provide: AppInsightService, useClass: AppInsightService },
    { provide: DocusignService, useClass: DocusignService },
    { provide: OfflineDetectionService, useClass: OfflineDetectionService },
    { provide: OverdraftCustomerService, useClass: OverdraftCustomerService },
    { provide: UpdateService, useClass: UpdateService },
    { provide: WhitelabelService, useClass: WhitelabelService },
    { provide: BankfeedsService, useClass: BankfeedsService },
    { provide: BasiqService, useClass: BasiqService },
    { provide: BrokerflowService, useClass: BrokerflowService },
    { provide: PortalThemeService, useClass: PortalThemeService },
    { provide: HeartbeatService, useClass: HeartbeatService },
    { provide: 'ApplicationResolver', useClass: ApplicationResolver },
    { provide: 'AssetFinanceRatecardResolver', useClass: AssetFinanceRatecardResolver },
    { provide: 'BusinessLoansRatecardResolver', useClass: BusinessLoansRatecardResolver },
    { provide: 'BusinessOverdraftRatecardResolver', useClass: BusinessOverdraftRatecardResolver },
    { provide: 'InsurancePremiumRatecardResolver', useClass: InsurancePremiumRatecardResolver },
    { provide: 'UserResolver', useClass: UserResolver },
    { provide: 'CustomerUserResolver', useClass: CustomerUserResolver },
    { provide: 'RatecardsResolver', useClass: RatecardsResolver },
    { provide: 'AggregatorResolver', useClass: AggregatorResolver },
    { provide: 'OriginatorBusinessResolver', useClass: OriginatorBusinessResolver },
    { provide: 'AccreditationResolver', useClass: AccreditationResolver },
    { provide: 'BankStatementsUploadResolver', useClass: BankStatementsUploadResolver },
    { provide: 'CreditFlowResolver', useClass: CreditFlowResolver },
    { provide: 'IndustryService', useClass: IndustryService },
    { provide: 'CronJobService', useClass: CronJobService },
    {
      provide: APP_INITIALIZER, useFactory: appInitializerFn, deps: [
        Injector,
        UpdateService,
        WhitelabelService,
        PortalThemeService,
        HeartbeatService,
        AuthService,
        IndustryService,
        CronJobService,
      ], multi: true
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker.enable,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideHotToastConfig(),
  ]
})
  .catch(err => console.error(err));
